/**
 * Auditoría de escrituras — owner-only combined view over every write-audit
 * store (payments, anticipos, cashbox, service, price, physcount, reception,
 * terminal, operator). Answers "who wrote what, when, and did it succeed"
 * across the whole write layer in one table, with a per-feature filter and a
 * CSV export for offline review. Read-only: this page never mutates anything.
 */
import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Badge, Button, Card, Select, SelectItem,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
  Text, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import { getWriteAudit } from '../api/writeaudit';
import type { WriteAuditEntry } from '../api/writeaudit';
import AccessDenied from '../components/AccessDenied';
import Header from '../components/Header';
import { usePagination } from '../lib/usePagination';
import Pagination from '../components/Pagination';
import { t } from '../lib/i18n';
import type { User } from '../api/types';

// Common columns get their own cells; everything else the store recorded is
// summarized in the "detalle" column so no field is ever invisible.
const COMMON = new Set(['feature', 'at', 'user', 'action', 'result']);

function detailOf(e: WriteAuditEntry): string {
  return Object.entries(e)
    .filter(([k, v]) => !COMMON.has(k) && v !== null && v !== '' && v !== 0)
    .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join('  ');
}

function fmtAt(at?: string): string {
  if (!at) return '';
  const d = new Date(at);
  return isNaN(d.getTime()) ? at : d.toLocaleString('es-MX');
}

function toCsv(entries: WriteAuditEntry[]): string {
  const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const lines = [['fecha', 'modulo', 'usuario', 'accion', 'resultado', 'detalle'].join(',')];
  for (const e of entries) {
    lines.push([
      esc(e.at ?? ''), esc(e.feature), esc(e.user ?? ''),
      esc(e.action ?? ''), esc(e.result ?? ''), esc(detailOf(e)),
    ].join(','));
  }
  return lines.join('\n');
}

export default function AdminWriteAudit() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);

  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'owner') {
    return (
      <>
        <Header user={user} />
        <main className="flex-1 p-4"><AccessDenied user={user} requiredRoleLabel={t.roles.owner} /></main>
      </>
    );
  }
  return <AdminWriteAuditInner user={user} />;
}

function AdminWriteAuditInner({ user }: { user: User }) {
  const [features, setFeatures] = useState<string[]>([]);
  const [entries, setEntries] = useState<WriteAuditEntry[]>([]);
  const pg = usePagination(entries);
  const [readErrors, setReadErrors] = useState<string[]>([]);
  const [feature, setFeature] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback((f: string) => {
    setLoading(true);
    setErr(null);
    getWriteAudit(f || undefined, 500)
      .then((res) => {
        setFeatures(res.features);
        setEntries(res.entries);
        setReadErrors(res.read_errors ?? []);
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(feature); }, [feature, load]);

  const exportCsv = () => {
    const blob = new Blob(['﻿' + toCsv(entries)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `auditoria_escrituras_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Header user={user} />
      <main className="mx-auto max-w-7xl px-4 py-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Title>{t.writeAudit.title}</Title>
            <Text>{t.writeAudit.subtitle}</Text>
          </div>
          <div className="flex items-end gap-2">
            <div className="w-48">
              <Select value={feature} onValueChange={setFeature} placeholder={t.writeAudit.allFeatures}>
                <SelectItem value="">{t.writeAudit.allFeatures}</SelectItem>
                {features.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </Select>
            </div>
            <Button variant="secondary" onClick={() => load(feature)}>{t.writeAudit.refresh}</Button>
            <Button variant="secondary" onClick={exportCsv} disabled={entries.length === 0}>
              {t.writeAudit.downloadCsv}
            </Button>
          </div>
        </div>

        {readErrors.length > 0 && (
          <Text className="mt-2 text-amber-600">
            {t.writeAudit.readErrors}: {readErrors.join(', ')}
          </Text>
        )}
        {err && <Text className="mt-2 text-red-600">{err}</Text>}

        <Card className="mt-4">
          {loading ? (
            <Text>{t.writeAudit.loading}</Text>
          ) : entries.length === 0 ? (
            <Text>{t.writeAudit.empty}</Text>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.writeAudit.colAt}</TableHeaderCell>
                    <TableHeaderCell>{t.writeAudit.colFeature}</TableHeaderCell>
                    <TableHeaderCell>{t.writeAudit.colUser}</TableHeaderCell>
                    <TableHeaderCell>{t.writeAudit.colAction}</TableHeaderCell>
                    <TableHeaderCell>{t.writeAudit.colResult}</TableHeaderCell>
                    <TableHeaderCell>{t.writeAudit.colDetail}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pg.pageRows.map((e, i) => (
                    <TableRow key={pg.from + i}>
                      <TableCell className="whitespace-nowrap">{fmtAt(e.at)}</TableCell>
                      <TableCell><Badge color="blue">{e.feature}</Badge></TableCell>
                      <TableCell>{e.user ?? ''}</TableCell>
                      <TableCell>{e.action ?? ''}</TableCell>
                      <TableCell>
                        <Badge color={e.result === 'ok' ? 'emerald' : 'red'}>
                          {e.result ?? ''}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xl truncate" title={detailOf(e)}>
                        {detailOf(e)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination {...pg} className="px-4" />
            </div>
          )}
        </Card>
      </main>
    </>
  );
}
